import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Clock, Wallet, Users, Briefcase, ArrowUpRight, type LucideIcon } from 'lucide-react';
import KPICard, { type KPIMetric } from './KpiCard';
import type { DashboardScope } from '../DashboardContext';

export type KPISectionProps = {
  primaryKPIs: KPIMetric[];
  secondaryKPIs: {
    attendance: KPIMetric[];
    payroll: KPIMetric[];
    employees: KPIMetric[];
    projects: KPIMetric[];
  };
  scope?: DashboardScope;
  /** Set when the caller may open the projects dashboard; the projects group header then links to it. */
  projectsDashboardHref?: string;
};

type GroupKey = 'attendance' | 'payroll' | 'employees' | 'projects';

const groupMeta: Record<GroupKey, { icon: LucideIcon; labelKey: string; cols: string }> = {
  attendance: { icon: Clock, labelKey: 'Attendance', cols: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3' },
  payroll: { icon: Wallet, labelKey: 'Payroll', cols: 'grid-cols-1 md:grid-cols-2' },
  employees: { icon: Users, labelKey: 'Employees', cols: 'grid-cols-1 md:grid-cols-2' },
  projects: { icon: Briefcase, labelKey: 'Projects', cols: 'grid-cols-1 md:grid-cols-2' },
};

const KPISection: React.FC<KPISectionProps> = ({ primaryKPIs, secondaryKPIs, scope, projectsDashboardHref }) => {
  const { t } = useTranslation();
  // CO-978: on the personal home the group counts the caller's own tasks, so its title says so.
  const projectsLabelKey = scope === 'personal' ? 'projects.my_group' : groupMeta.projects.labelKey;

  const renderGroup = (key: GroupKey, items: KPIMetric[]) => {
    if (!items?.length) return null;
    const { icon: GroupIcon, cols } = groupMeta[key];
    const labelKey = key === 'projects' ? projectsLabelKey : groupMeta[key].labelKey;
    return (
      <div data-testid={`kpi-group-${key}`}>
        <div className="flex items-center gap-2 mb-3 ps-1">
          <GroupIcon className="w-3.5 h-3.5 text-muted-foreground/70" strokeWidth={2.25} />
          <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">
            {t(labelKey)}
          </h3>
          <div className="flex-1 h-px bg-border/60" />
          {key === 'projects' && projectsDashboardHref && (
            <Link
              to={projectsDashboardHref}
              className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.98]"
              data-testid="kpi-group-projects-link"
            >
              {t('projects:dashboard.homeLink')}
              <ArrowUpRight className="h-3.5 w-3.5 rtl:-scale-x-100" aria-hidden="true" />
            </Link>
          )}
        </div>
        <div className={`grid gap-4 ${cols}`}>
          {items.map((metric) => (
            <KPICard key={metric?.id} metric={metric} isPrimary={false} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-7" data-testid="kpi-section">
      {/* Primary KPIs - Tier 1 */}
      {primaryKPIs?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="primary-kpis">
          {primaryKPIs.map((metric) => (
            <KPICard key={metric?.id} metric={metric} isPrimary={true} />
          ))}
        </div>
      )}

      {/* Secondary KPIs - Tier 2 (Grouped) */}
      <div className="space-y-6" data-testid="secondary-kpis">
        {renderGroup('attendance', secondaryKPIs?.attendance)}
        {renderGroup('payroll', secondaryKPIs?.payroll)}
        {renderGroup('employees', secondaryKPIs?.employees)}
        {renderGroup('projects', secondaryKPIs?.projects)}
      </div>
    </div>
  );
};

export default KPISection;
